import { useEffect, useRef, type ReactNode } from 'react'
import { ChevronDownIcon } from '@/components/Icons'

/**
 * Icon-only control to collapse live-meeting chrome on viewports below `lg`.
 */
export function ChromeHideButton({
  onHide,
  hideLabel,
  hideIconDirection = 'up',
  testId,
  className,
}: {
  onHide: () => void
  hideLabel: string
  hideIconDirection?: 'up' | 'down'
  testId?: string
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onHide}
      aria-label={hideLabel}
      title={hideLabel}
      data-testid={testId}
      className={[
        'flex h-7 w-7 shrink-0 touch-manipulation items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-[var(--text-tertiary)] dark:hover:bg-[var(--bg-tertiary)] dark:hover:text-[var(--text-secondary)] lg:hidden',
        className ?? '',
      ].join(' ')}
    >
      <ChevronDownIcon
        className={['h-4 w-4', hideIconDirection === 'up' ? 'rotate-180' : ''].join(' ')}
      />
    </button>
  )
}

/**
 * Grid-row collapse for live-meeting chrome (header / footer) on viewports
 * below `lg`. Keeps the flex height chain intact — no fixed heights — so
 * MeetingInsights can expand into freed space.
 */
export function CollapsibleChrome({
  open,
  children,
  testId,
}: {
  open: boolean
  children: ReactNode
  testId?: string
}) {
  const rootRef = useRef<HTMLDivElement>(null)

  // React 18 does not type/pass the `inert` JSX prop; set the DOM property so
  // collapsed chrome (e.g. End/pause/mute) leaves the tab order and ignores clicks.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    el.inert = !open
  }, [open])

  return (
    <div
      ref={rootRef}
      className={[
        'grid shrink-0 transition-[grid-template-rows] duration-200 ease-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      ].join(' ')}
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
      aria-hidden={!open}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  )
}

/**
 * Compact in-flow strip shown when chrome is collapsed on mobile. Stays in the
 * document flow so it does not overlay JumpToLiveButton inside scroll panels.
 */
export function ChromeRestoreStrip({
  onShow,
  showLabel,
  trailing,
  testId,
}: {
  onShow: () => void
  showLabel: string
  trailing?: ReactNode
  testId?: string
}) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 border-b border-blue-100 bg-blue-50/40 px-2 py-1.5 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] lg:hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onShow}
        aria-label={showLabel}
        title={showLabel}
        className="inline-flex min-h-[32px] min-w-0 flex-1 touch-manipulation items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100/60 dark:text-[var(--accent)] dark:hover:bg-[var(--accent-light)]"
      >
        <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{showLabel}</span>
      </button>
      {trailing ? <div className="flex shrink-0 items-center gap-1.5">{trailing}</div> : null}
    </div>
  )
}
